import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, TextField } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import React, { useCallback, useEffect, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Card, CardBody, CardHeader, CardHeaderToolbar } from '../../../../_metronic/_partials/controls'
import { getCustomers } from '../../../../api/customer'
import { deleteOfferImport, getOfferImports, postOfferImport } from '../../../../api/offerImport'
import { alertError, alertSuccess } from '../../../../utils/logger'
import Table from '../../../components/tables/table'

const emptyImport = () => ({ cliente: null, archivo: null, crearPartidasEspecificasCliente: true })

function formatBytes(bytes) {
	if (!bytes)
		return '0 B'
	const units = ['B', 'KB', 'MB', 'GB']
	const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
	return `${(bytes / Math.pow(1024, index)).toFixed(index ? 1 : 0)} ${units[index]}`
}

export default function OfferImportsPage() {
	const loggedUser = useSelector((store) => store.authentication?.user, shallowEqual)
	const accessToken = loggedUser?.accessToken
	const history = useHistory()
	const [imports, setImports] = useState([])
	const [customers, setCustomers] = useState([])
	const [editor, setEditor] = useState(null)
	const [saving, setSaving] = useState(false)
	const [deleting, setDeleting] = useState(null)
	const [deleteBusy, setDeleteBusy] = useState(false)
	const [deleteError, setDeleteError] = useState('')

	const load = useCallback(async () => {
		try {
			const [importsResult, customersResult] = await Promise.all([getOfferImports(accessToken), getCustomers(accessToken)])
			setImports(importsResult.data || [])
			setCustomers(customersResult.data || [])
		} catch (error) {
			alertError({ error, customMessage: 'No se han podido cargar las importaciones de ofertas.' })
		}
	}, [accessToken])

	useEffect(() => { load() }, [load])

	const save = async () => {
		if (!editor?.cliente?.id || !editor?.archivo) {
			alertError({ customMessage: 'Selecciona un cliente y un archivo Excel .xlsx.' })
			return
		}
		if (!editor.archivo.name.toLowerCase().endsWith('.xlsx')) {
			alertError({ customMessage: 'Por ahora solo se admiten archivos Excel .xlsx.' })
			return
		}
		setSaving(true)
		try {
			const response = await postOfferImport(editor.cliente.id, editor.archivo, editor.crearPartidasEspecificasCliente, accessToken)
			alertSuccess({ title: 'Excel recibido', customMessage: 'El agente analizará el Excel completo. Puedes salir y volver cuando quieras.' })
			setEditor(null)
			await load()
			history.push(`/ofertas/importaciones/${response.data.id}`)
		} catch (error) {
			alertError({ error, customMessage: 'No se ha podido preparar la importación.' })
		} finally {
			setSaving(false)
		}
	}

	const remove = async () => {
		setDeleteBusy(true); setDeleteError('')
		try {
			await deleteOfferImport(deleting.id, accessToken)
			setDeleting(null)
			await load()
		} catch (error) {
			setDeleteError(typeof error.response?.data === 'string' ? error.response.data : error.response?.data?.message || 'No se ha podido eliminar la importación.')
		} finally { setDeleteBusy(false) }
	}
	const columns = [
		{ dataField: 'archivo_nombre', text: 'Archivo', sort: true },
		{ dataField: 'cliente_nombre', text: 'Cliente', sort: true },
		{ dataField: 'estado', text: 'Estado', sort: true },
		{ dataField: 'partidas', text: 'Partidas detectadas', align: 'center' },
		{ dataField: 'tamano', text: 'Tamaño', align: 'right' },
		{ dataField: 'fecha', text: 'Creada', sort: true },
		{ dataField: 'acciones', text: '', formatter: (_, row) => <Button color="secondary" size="small" variant="outlined" disabled={row.estado === 'Analizando'} title={row.estado === 'Analizando' ? 'Espera a que termine el análisis' : 'Eliminar importación'} onClick={(event) => { event.stopPropagation(); setDeleteError(''); setDeleting(row) }}>Eliminar</Button> },
	]
	const rows = imports.map((item) => ({
		...item,
		resumen_extraccion: item.error || item.resumen_extraccion,
		partidas: item.partidas > 0 ? item.partidas : item.estado === 'Error' ? 'Sin resultado válido' : item.estado === 'Analizando' || item.analisis_solicitado ? 'Analizando...' : 'Pendiente de análisis',
		tamano: formatBytes(item.archivo_tamano),
		fecha: item.fecha_alta ? new Date(item.fecha_alta).toLocaleString('es-ES') : '',
	}))

	return <>
		<Card>
			<CardHeader title="Importaciones de ofertas">
				<CardHeaderToolbar>
					<Button color="primary" variant="contained" onClick={() => setEditor(emptyImport())}>Nueva importación</Button>
				</CardHeaderToolbar>
			</CardHeader>
			<CardBody>
				<div className="text-muted mb-5">
          Cada importación conserva el Excel original y se revisa antes de crear una oferta. El análisis no modifica plantillas ni ofertas existentes.
				</div>
				<div className="alert alert-light-primary mb-5">
					Selecciona cliente y Excel. La IA propondrá capítulos, partidas y plantillas candidatas. Revisa cada decisión y genera la oferta cuando todo esté validado.
				</div>
				<Table data={rows} columns={columns} rowStyle={{ cursor: 'pointer' }} rowEvents={{ onClick: (_, row) => history.push(`/ofertas/importaciones/${row.id}`) }} />
			</CardBody>
		</Card>
		<Dialog open={Boolean(deleting)} onClose={() => !deleteBusy && setDeleting(null)} fullWidth maxWidth="sm">
			<DialogTitle>Eliminar importación</DialogTitle>
			<DialogContent>
				<p>¿Eliminar «{deleting?.archivo_nombre}»? Se eliminarán su copia del Excel, estructura y decisiones guardadas. Esta acción no se puede deshacer.</p>
				{deleting?.oferta_id && <p>La oferta ya generada y sus plantillas se conservarán. Este botón solo elimina la importación.</p>}
				{deleteError && <div role="alert" className="alert alert-danger">{deleteError}</div>}
			</DialogContent>
			<DialogActions><Button disabled={deleteBusy} onClick={() => setDeleting(null)}>Cancelar</Button><Button disabled={deleteBusy} color="secondary" variant="contained" onClick={remove}>{deleteBusy ? 'Eliminando...' : 'Eliminar importación'}</Button></DialogActions>
		</Dialog>

		<Dialog open={Boolean(editor)} onClose={() => !saving && setEditor(null)} fullWidth maxWidth="md">
			<DialogTitle>Nueva importación de oferta</DialogTitle>
			<DialogContent dividers>
				<div className="text-muted mb-4">El Excel completo se enviará al agente IA configurado, que podrá consultar categorías y plantillas generales o del cliente. No se creará una oferta hasta que valides la propuesta.</div>
				<Autocomplete
					options={customers}
					getOptionLabel={(option) => option?.nombre || ''}
					value={editor?.cliente || null}
					onChange={(_, cliente) => setEditor((current) => ({ ...current, cliente }))}
					renderInput={(params) => <TextField {...params} label="Cliente" required variant="outlined" margin="dense" />}
				/>
				<FormControlLabel
					className="mt-3"
					control={<Checkbox color="primary" checked={editor?.crearPartidasEspecificasCliente ?? true} onChange={(event) => setEditor((current) => ({ ...current, crearPartidasEspecificasCliente: event.target.checked }))} disabled={saving} />}
					label="Crear las partidas como partidas exclusivas del cliente"
				/>
				<div className="text-muted ml-8">Si lo desmarcas, se crearán como plantillas generales disponibles para todos los clientes. Podrás cambiarlo en cada partida antes de generar la oferta.</div>
				<div className="mt-5 p-6 rounded" style={{ border: '2px dashed #c9c3ed', background: '#f8f7fc', minHeight: 145 }}>
					<div className="font-weight-bold mb-3">Archivo Excel</div>
					<Button component="label" variant="outlined" color="primary" disabled={saving}>
						Seleccionar Excel
						<input type="file" hidden accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(event) => setEditor((current) => ({ ...current, archivo: event.target.files?.[0] || null }))} />
					</Button>
					<div className="mt-3" style={{ overflowWrap: 'anywhere' }}>{editor?.archivo ? `${editor.archivo.name} · ${formatBytes(editor.archivo.size)}` : 'Ningún archivo seleccionado. Formato .xlsx, máximo 50 MB.'}</div>
				</div>
			</DialogContent>
			<DialogActions>
				<Button disabled={saving} onClick={() => setEditor(null)}>Cancelar</Button>
				<Button color="primary" variant="contained" disabled={saving || !editor?.cliente || !editor?.archivo} onClick={save}>{saving ? 'Subiendo Excel...' : 'Importar y analizar con IA'}</Button>
			</DialogActions>
		</Dialog>
	</>
}
